import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import { checkInvalidStartDate } from '../../utils/cart';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Quote } from '../../types/quotes';
import {
  CartModalPayload,
  CartState,
  SetRentalProtectionPayload,
  SetShiftPayload,
  SetCartHeaderOpenPayload,
  SetCartBackendSyncStatusPayload,
  SetUserUuidPayload,
  SetCartItemPayload,
  SetActiveRentalProtectionPayload,
  SetActiveShiftPayload,
  SiteLevelDate,
  ValidateCartDatesPayload,
  SalesStaffDetails,
} from '../../types/cart-state';
import { CartItem, CartModalConfigType, CartOperationType } from '../../types/cart';

dayjs.extend(tz);

const initialState: CartState = {
  backendSyncStatus: null,
  userUuid: null,
  cartItems: [],
  timezone: dayjs.tz.guess(),
  isCartValid: true,
  isTaxExempt: false,
  isCartActivated: false,
  loadingFlags: {
    isLoadingShift: true,
    isLoadingRPP: true,
    isLoadingQuote: true,
    isLoadingTaxExemptStatus: true,
  },
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    openCartModal: (state, action: PayloadAction<CartModalPayload>) => {
      const cartModalConfig: CartModalConfigType = {
        equipmentId: action.payload.cartItem.equipmentId,
        cartOperationType: action.payload.cartOperationType,
        imageSource: action.payload.cartItem.imageSource,
        equipmentClassName: action.payload.cartItem.name,
        quantity: action.payload.cartItem.quantity ?? 1,
        slug: action.payload.cartItem.slug,
        open: true,
        toggleCartHeaderOnUpdate: action.payload.toggleCartHeaderOnUpdate,
      };
      if (action.payload.cartOperationType === CartOperationType.UPDATE) {
        cartModalConfig.index = action.payload.index;
        cartModalConfig.startDate = action.payload.cartItem.startDate;
        cartModalConfig.endDate = action.payload.cartItem.endDate;
      }
      state.cartModalConfig = cartModalConfig;
      state.cartHeaderConfig = undefined;
    },
    closeCartModal: (state) => {
      state.cartModalConfig = undefined;
    },
    toggleCartHeaderDropdown: (state, action: PayloadAction<SetCartHeaderOpenPayload>) => {
      state.cartHeaderConfig = {
        open: action.payload.open,
        index: action.payload.index,
        maxItemsToShow: 3,
      };
    },
    setUserUuid: (state, action: PayloadAction<SetUserUuidPayload>) => {
      state.userUuid = action.payload.id;
    },
    setCartItem: (state, action: PayloadAction<SetCartItemPayload>) => {
      let cartUpdateObject: CartItem;
      let index: number;
      state.timezone = dayjs.tz.guess();
      switch (action.payload.actionType) {
        case CartOperationType.ADD:
          cartUpdateObject = action.payload.cartUpdateObject as CartItem;
          state.cartItems.push(cartUpdateObject);
          break;
        case CartOperationType.UPDATE:
          cartUpdateObject = action.payload.cartUpdateObject as CartItem;
          index = Number(action.payload.index);
          if (index >= 0 && index < state.cartItems.length) {
            state.cartItems[index] = cartUpdateObject;
          }
          break;
        case CartOperationType.REMOVE:
          index = Number(action.payload.index);
          if (index >= 0 && index < state.cartItems.length) {
            state.cartItems.splice(index, 1);
          }
          break;
        default:
          break;
      }
    },
    setBackendSyncLoading: (state, action: PayloadAction<SetCartBackendSyncStatusPayload>) => {
      state.backendSyncStatus = action.payload.status;
    },
    setBackendSyncFulfilled: (state) => {
      state.backendSyncStatus = null;
    },
    setBackendSyncFailed: (state) => {
      state.backendSyncStatus = null;
    },
    setQuoteLoading: (state) => {
      state.loadingFlags.isLoadingQuote = true;
    },
    setQuoteError: (state) => {
      state.loadingFlags.isLoadingQuote = false;
      state.quote = undefined;
    },
    setQuote: (state, action: PayloadAction<{ quote: Quote }>) => {
      state.loadingFlags.isLoadingQuote = false;
      state.quote = action.payload.quote;
    },
    setShift: (state, action: PayloadAction<SetShiftPayload>) => {
      state.shift = {
        ...state.shift,
        ...action.payload.shift,
      };
      state.loadingFlags.isLoadingShift = action.payload.isLoading;
    },
    setActiveShift: (state, action: PayloadAction<SetActiveShiftPayload>) => {
      if (state.shift) {
        state.shift.active = action.payload.active;
      }
    },
    setRentalProtection: (state, action: PayloadAction<SetRentalProtectionPayload>) => {
      state.rentalProtection = {
        ...state.rentalProtection,
        ...action.payload.rentalProtection,
      };
      state.loadingFlags.isLoadingRPP = action.payload.isLoading;
    },
    setActiveRentalProtection: (state, action: PayloadAction<SetActiveRentalProtectionPayload>) => {
      if (state.rentalProtection) {
        state.rentalProtection.active = action.payload.active;
      }
    },
    setSalesStaff: (state, action: PayloadAction<SalesStaffDetails | null>) => {
      state.salesStaffDetails = action.payload;
    },
    setSiteLevelDate: (
      state,
      action: PayloadAction<Omit<SiteLevelDate, 'updatedOn' | 'state'>>,
    ) => {
      state.siteLevelDate = {
        ...action.payload,
        updatedOn: dayjs().unix(),
      };
    },
    setSiteLevelStartDate: (state, action: PayloadAction<number>) => {
      state.siteLevelDate = {
        ...state.siteLevelDate,
        start: action.payload,
        updatedOn: dayjs().unix(),
      };
    },
    setSiteLevelEndDate: (state, action: PayloadAction<number>) => {
      state.siteLevelDate = {
        ...state.siteLevelDate,
        end: action.payload,
        updatedOn: dayjs().unix(),
      };
    },
    resetSiteLevelDate: (state) => {
      state.siteLevelDate = undefined;
    },
    validateAndSetDates: (state, action: PayloadAction<ValidateCartDatesPayload | undefined>) => {
      const updateDates = () => {
        if (!action.payload) return;

        const { isSiteLevelDateChange, startDate, endDate } = action.payload;
        const updatedStartDate = isSiteLevelDateChange ? state.siteLevelDate?.start : startDate;
        const updatedEndDate = isSiteLevelDateChange ? state.siteLevelDate?.end : endDate;

        if (!updatedStartDate || !updatedEndDate) return;

        if (!isSiteLevelDateChange) {
          state.siteLevelDate = {
            start: updatedStartDate,
            end: updatedEndDate,
            updatedOn: dayjs().unix(),
          };
        }
        state.cartItems.map((item) => {
          item.startDate = updatedStartDate as number;
          item.endDate = updatedEndDate as number;
          return item;
        });
      };

      updateDates();

      state.cartItems.map((item) => {
        item.isStartDateValid = !checkInvalidStartDate(item.startDate, state.timezone);
        return item;
      });
      state.isCartValid = state.cartItems.every((item) => item.isStartDateValid === true);
    },
    setTaxExemptLoading: (state) => {
      state.loadingFlags.isLoadingTaxExemptStatus = true;
    },
    setTaxExempt: (state, action: PayloadAction<boolean>) => {
      state.loadingFlags.isLoadingTaxExemptStatus = false;
      state.isTaxExempt = action.payload;
    },
    activateCart: (state) => {
      state.isCartActivated = true;
    },
    clearCart: (state) => {
      return {
        ...initialState,
        siteLevelDate: state.siteLevelDate,
        isCartActivated: state.isCartActivated,
      };
    },
  },
});

export const {
  openCartModal,
  closeCartModal,
  toggleCartHeaderDropdown,
  setUserUuid,
  setBackendSyncLoading,
  setCartItem,
  setBackendSyncFulfilled,
  setBackendSyncFailed,
  setQuote,
  setQuoteLoading,
  setQuoteError,
  setShift,
  setActiveShift,
  setRentalProtection,
  setActiveRentalProtection,
  setSalesStaff,
  setSiteLevelStartDate,
  setSiteLevelEndDate,
  setSiteLevelDate,
  resetSiteLevelDate,
  validateAndSetDates,
  setTaxExempt,
  setTaxExemptLoading,
  activateCart,
  clearCart,
} = cartSlice.actions;

export const updateCartBackendEpicAction = createAction<SetCartItemPayload>(
  'cart/updateBackendCartEpicAction',
);

export const onCartActivated = createAction('cart/onCartActivated');

export default cartSlice.reducer;
